<template>
  <div class="exp-price">
    <div
      v-if="showPriceDifference"
      class="former-price flex gap-1"
    >
      <span class="strike-through"> {{ article?.formerBruttoPrice }}€ </span>
      <span class="former-price-percent"> -{{ formerPriceDifference }}% </span>
    </div>
    <div
      v-else
      class="former-price-placeholder"
    ></div>
    <div
      v-if="fullPrice?.bruttoPrice"
      class="displayPrice"
    >
      <ArticlePubDataWrapper
        class="exp_price text-left text-3xl text-tertiary"
        :created-on="fullPrice?.createdOn"
        :last-updated="fullPrice?.lastUpdated"
        :cache-level="fullPrice?.cacheLevel"
        :pub-source="fullPrice?.pubSource"
      >
        {{
          fullPrice?.bruttoPrice % 1 === 0
            ? fullPrice?.bruttoPrice + '.-'
            : parseFloat(`${fullPrice?.bruttoPrice}`).toFixed(2)
        }}
      </ArticlePubDataWrapper>
    </div>
    <template v-if="withPromotions">
      <div
        v-if="promotionPrice?.checkoutPrice && promotionPrice.currentPrice !== promotionPrice.checkoutPrice"
        class="your-price text-tertiary"
      >
        <span class="your-price-text">Du zahlst nur:</span>
        <span class="your-price-value">{{
          promotionPrice?.checkoutPrice % 1 === 0
            ? promotionPrice?.checkoutPrice + '.-'
            : parseFloat(`${promotionPrice?.checkoutPrice}`).toFixed(2)
        }}</span>
      </div>
      <div
        v-else
        class="your-price-placeholder text-tertiary"
      ></div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { Article, FullPrice, PromotionPrice } from '@expert/types';
import { computedAsync } from '@vueuse/core';

interface Props {
  fullPrice: FullPrice;
  article: Article;
  withPromotions?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  withPromotions: false,
});

const formerPriceDifference: ComputedRef<number> = computed(() => {
  return Math.floor(100 - (props.fullPrice.bruttoPrice / props.article.formerBruttoPrice) * 100);
});

const showPriceDifference = computed(() => {
  const percentAboveMin = formerPriceDifference.value > 10;
  const percentBelowMax = formerPriceDifference.value < 70;

  return (
    percentBelowMax
    && ((props.fullPrice.bruttoPrice >= 50 && percentAboveMin)
      || props.article.formerBruttoPrice - props.fullPrice.bruttoPrice >= 5)
  );
});

const promotionPrice: Ref<PromotionPrice | undefined> = computedAsync(async () => {
  const articleId: string | undefined = props.article?.articleId;
  const fullPrice: FullPrice | undefined = props.fullPrice;

  if (!articleId || !fullPrice) {
    return undefined;
  }

  return getArticlePromotionPrice(articleId, fullPrice);
});
</script>

<style scoped>
/* AUSLAGERN; PARALLELEN ZU ExpPriceBasic*/
.former-price-percent {
  @apply bg-primary text-primary-contrast px-1 rounded-full;
}

.former-price {
  font-family: foundationsans-blackcd, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 18px;
}

.former-price-placeholder {
  height: 18px;
}

.your-price {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: foundationsans-blackcd, sans-serif;
  background-color: rgb(var(--color-secondary));
  padding: 4px;
  border-radius: 8px;
}

.your-price-text {
  font-size: 1rem;
  line-height: 1rem;
}

.your-price-value {
  font-size: 1.5rem;
  line-height: 1.5rem;
}

.your-price-placeholder {
  height: 16px;
  margin: 4px;
}

.exp_price {
  font-size: 40px;
}
</style>
